import { useCommandState } from "cmdk";
import { useEffect, useRef, type RefObject } from "react";

const ROOT = "[cmdk-root]";
const ANY_ITEM = "[cmdk-item]";

function press(root: Element, key: string) {
  root.dispatchEvent(new KeyboardEvent("keydown", { key, bubbles: true }));
}

/**
 * Select the first item when server results arrive, as cmdk does when the query changes, and
 * keep an Enter that came too early.
 *
 * Results mount after the query changed, so cmdk may have selected a local match further down
 * (or nothing, and then Enter does nothing). Once they are on screen, this asks cmdk to select
 * its first item, exactly as pressing Home in the palette does. Someone who types a reference
 * and presses Enter straight away, while the server is still searching and nothing is selected,
 * gets the first result opened as soon as it arrives.
 */
export function useSelectFirstResult(
  anchor: RefObject<HTMLElement | null>,
  results: unknown,
  loading: boolean,
) {
  const selected = useCommandState((state) => state.value);
  const enterPending = useRef(false);

  useEffect(() => {
    const root = anchor.current?.closest(ROOT);
    if (!root || !loading || selected) return;
    const onKeyDown = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key === "Enter") enterPending.current = true;
    };
    root.addEventListener("keydown", onKeyDown);
    return () => {
      root.removeEventListener("keydown", onKeyDown);
    };
  }, [anchor, loading, selected]);

  useEffect(() => {
    if (results === undefined) return;
    const frame = requestAnimationFrame(() => {
      const root = anchor.current?.closest(ROOT);
      const openFirst = enterPending.current;
      enterPending.current = false;
      if (!root?.querySelector(ANY_ITEM)) return;
      press(root, "Home");
      if (openFirst) press(root, "Enter");
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [anchor, results]);
}
